// One agronomic family of indices — the Vigour & canopy / Nutrition /
// Moisture / Thermal tabs of the Block Dock.
//
// Replaces the single "Index" tab, whose pill row listed every index with no
// values and no grouping. A family tab answers one question, so it can afford
// to show every member's current reading and its change side by side (the
// comparison the flat row lost) and chart whichever member you pick.
//
// It also has room to say what each index IS and what its number MEANS, which
// no surface in the console did: the reference column carries a definition and
// a scale per index, and the charted reading is turned into a sentence via
// INDEX_BANDS rather than left as a bare decimal.
//
// ⚠️ Ten of the thirteen tiles used to be DISABLED here, labelled "Grid only",
// on the belief that `/blocks/{id}/indices/{code}/timeseries` served only
// ndvi/ndre/ndwi. It never did — that is the farm-wide summary endpoint
// (`blocks_summary_router._MAP_INDICES`), which the dock does not call. The
// route reads `block_index_daily` by `index_code` with no allowed-list. Every
// tile now fetches its own series; see dockSeries.ts.
import { useQueries, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { getIndexCatalog, type AnyIndexCode as ApiIndexCode } from "@/api/indices";
import { formatIndexTick, formatIndexValue } from "@/lib/indexFormat";
import {
  FAMILY_PRIMARY,
  HEALTH_DOT,
  INDEX_FAMILIES,
  INDEX_META,
  isThermalIndex,
  type IndexFamilyKey,
} from "./constants";
import { bandFor, indexDeltaLabel, isoDay, isoDaysBefore, shortDate } from "./dockFormat";
import {
  deltaOver,
  deltaPctOver,
  dockSeriesOptions,
  latestValue,
  toPoints,
  type SeriesPoint,
} from "./dockSeries";
import { Dot } from "./ui";

const PRESETS: { days: number; key: string }[] = [
  { days: 30, key: "d30" },
  { days: 90, key: "d90" },
  { days: 365, key: "season" },
];

/** Window the tile deltas are measured over. Kept at the 7 days the cards
 *  always claimed, independent of the chart range — a card that says "7 days"
 *  under a 365-day chart must still mean 7 days. */
const CARD_DELTA_DAYS = 7;

/** Native resolution of the thermal sensor, for the caveat the tab prints
 *  once. Same number the pixel console's legend states. */
const THERMAL_METRES = 100;

type Point = SeriesPoint;

function Chart({ points, unit }: { points: Point[]; unit: string }): ReactNode {
  const { t, i18n } = useTranslation("farmConsole");
  const lang = i18n.language;
  if (points.length < 2) {
    return (
      <div className="flex h-[168px] items-center justify-center rounded-xl border border-ap-line text-sm text-ap-muted">
        {t("dock.index.notEnough")}
      </div>
    );
  }
  const W = 620;
  const H = 168;
  const padL = 36;
  const padR = 12;
  const padT = 10;
  const padB = 22;
  const vals = points.map((p) => p.value);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const pad = (hi - lo) * 0.35 || 0.05;
  const yMin = lo - pad;
  const yMax = hi + pad;
  const X = (i: number): number => padL + (i / (points.length - 1)) * (W - padL - padR);
  const Y = (v: number): number => padT + (1 - (v - yMin) / (yMax - yMin)) * (H - padT - padB);

  const line = points
    .map((p, i) => `${i ? "L" : "M"}${X(i).toFixed(1)} ${Y(p.value).toFixed(1)}`)
    .join(" ");
  // Ticks carry the index's own precision: three decimals of a ratio, one of
  // a temperature. `43.00` and `43.000` both overstate a 100 m thermal pixel.
  const gridlines = [0, 1, 2, 3, 4].map((g) => {
    const v = yMin + (g / 4) * (yMax - yMin);
    return { y: Y(v), label: formatIndexTick(v, unit) };
  });
  const ticks = [0, Math.floor((points.length - 1) / 2), points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-[168px] w-full"
      role="img"
      aria-label={t("dock.index.chartLabel")}
    >
      {gridlines.map((g) => (
        <g key={g.label}>
          <line
            x1={padL}
            x2={W - padR}
            y1={g.y}
            y2={g.y}
            stroke="currentColor"
            className="text-ap-line"
            strokeWidth={1}
          />
          <text x={padL - 6} y={g.y + 3} textAnchor="end" className="fill-ap-muted text-[9px]">
            {g.label}
          </text>
        </g>
      ))}
      <path
        d={line}
        fill="none"
        stroke={HEALTH_DOT.healthy}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={X(points.length - 1)}
        cy={Y(points[points.length - 1].value)}
        r={3.5}
        fill={HEALTH_DOT.healthy}
      />
      {ticks.map((i, k) => (
        <text
          key={i}
          x={X(i)}
          y={H - 6}
          textAnchor={k === 0 ? "start" : k === 2 ? "end" : "middle"}
          className="fill-ap-muted text-[9px]"
        >
          {shortDate(points[i].time, lang)}
        </text>
      ))}
    </svg>
  );
}

/** One index in the family: its latest reading, how it moved, and a click that
 * charts it here.
 *
 * Every member is selectable now. Ten of the thirteen used to render disabled
 * at 45% opacity with an em dash and the words "Grid only" — a statement about
 * the API that was not true (see the file header). The only thing that still
 * varies between members is whether the MAP can paint this index, which is a
 * per-console fact the parent passes in: the pixel console draws the index COG
 * itself and can draw all thirteen, while the live console draws the sub-block
 * grid mesh, and `grid_configs` exist only for the optical product. A card the
 * map cannot honour still charts, and says so instead of silently not
 * repainting.
 */
function IndexCard({
  code,
  points,
  loading,
  unit,
  charted,
  onMap,
  paintable,
  onSelect,
}: {
  code: ApiIndexCode;
  points: SeriesPoint[];
  loading: boolean;
  unit: string;
  /** This card is the one the tab is plotting. */
  charted: boolean;
  /** This card is the index the map is painted by. */
  onMap: boolean;
  paintable: boolean;
  onSelect: () => void;
}): ReactNode {
  const { t } = useTranslation("farmConsole");
  const current = latestValue(points);
  const d = indexDeltaLabel(code, deltaOver(points, CARD_DELTA_DAYS), unit);
  const empty = !loading && current == null;
  const title = [t(`dock.meaning.${code}`), paintable ? null : t("dock.notOnThisMap")]
    .filter(Boolean)
    .join(" — ");

  return (
    <button
      type="button"
      aria-pressed={charted}
      title={title}
      onClick={onSelect}
      className={clsx(
        "relative flex min-w-[104px] flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-start",
        charted
          ? "border-ap-primary bg-ap-primary-soft"
          : "border-ap-line hover:bg-ap-primary-soft/50",
      )}
    >
      <Dot color={empty ? HEALTH_DOT.unknown : d.color} className="absolute end-2.5 top-2.5" />
      <span className="flex items-center gap-1 text-xs font-bold tracking-wide text-ap-ink">
        {INDEX_META[code].label}
        {/* The map's current index, marked on the card rather than only in the
            title bar — the two used to be readable only apart. */}
        {onMap ? <span aria-label={t("dock.onMap")}>◉</span> : null}
      </span>
      <span className="text-lg font-bold leading-tight tabular-nums text-ap-ink">
        {loading ? "…" : formatIndexValue(current, unit)}
      </span>
      <span className="text-xs font-bold" style={{ color: empty ? HEALTH_DOT.unknown : d.color }}>
        {empty ? t("dock.noReading") : `${d.arrow} ${d.text}`}
      </span>
    </button>
  );
}

export function DockFamilyView({
  blockId,
  family,
  activeIndex,
  paintableIndices,
  onActiveIndexChange,
}: {
  blockId: string;
  family: IndexFamilyKey;
  /** What the map is currently painted by. Marked on the matching card; not
   *  what this tab charts. */
  activeIndex: ApiIndexCode;
  /** Indices THIS console's map can draw. The live console draws the
   *  sub-block grid mesh, which exists for the optical product only; the pixel
   *  console draws the index raster and can draw all thirteen. */
  paintableIndices: readonly ApiIndexCode[];
  onActiveIndexChange: (c: ApiIndexCode) => void;
}): ReactNode {
  const { t } = useTranslation("farmConsole");
  const [from, setFrom] = useState(() => isoDaysBefore(30));
  const [to, setTo] = useState(() => isoDay());

  const members = useMemo(
    () => INDEX_FAMILIES.find((f) => f.key === family)?.indices ?? [],
    [family],
  );

  // What this tab plots. It opens on the family's usual index, or on the map's
  // index when that belongs to this family, so arriving here from "open this
  // family" charts what the reader was already looking at. Clicking a card
  // moves it — reading a tab never repaints the map on its own.
  const [charted, setCharted] = useState<ApiIndexCode>(() =>
    members.includes(activeIndex) ? activeIndex : FAMILY_PRIMARY[family],
  );
  useEffect(() => {
    setCharted((c) => (members.includes(c) ? c : FAMILY_PRIMARY[family]));
  }, [members, family]);

  const paintable = useMemo(() => new Set(paintableIndices), [paintableIndices]);

  // Units are platform-wide curated data (`public.indices_catalog`), cached
  // for an hour across every farm and index. Read rather than hardcoded: a
  // local code-to-unit table is the mirror that drifts. A failure degrades to
  // the dimensionless case, which is right for twelve of the thirteen.
  const catalogQ = useQuery({
    queryKey: ["indices", "catalog"] as const,
    queryFn: getIndexCatalog,
    staleTime: 60 * 60_000,
  });
  const unitFor = useMemo(() => {
    const byCode = new Map((catalogQ.data ?? []).map((e) => [e.code, e.unit ?? ""]));
    return (code: ApiIndexCode): string => byCode.get(code) ?? "";
  }, [catalogQ.data]);

  // One query per member of the OPEN tab — two to five requests, and only for
  // the family being read. The charted index's query is the same entry the
  // chart reads, so plotting costs nothing beyond the card.
  const seriesQs = useQueries({
    queries: members.map((code) => dockSeriesOptions(blockId, code, from, to)),
  });
  const seriesData = seriesQs.map((q) => q.data);
  const pointsByCode = useMemo(() => {
    const m = new Map<ApiIndexCode, SeriesPoint[]>();
    members.forEach((code, i) => m.set(code, toPoints(seriesData[i])));
    return m;
    // `seriesQs` is a fresh array every render; the payloads inside it are
    // what actually change, so the deps are spread rather than the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, ...seriesData]);

  const chartedIdx = members.indexOf(charted);
  const chartedQ = chartedIdx >= 0 ? seriesQs[chartedIdx] : undefined;
  const points = pointsByCode.get(charted) ?? [];
  const chartedUnit = unitFor(charted);

  const current = latestValue(points);
  // Change over the trailing 14 days of whatever window is on screen.
  const delta = deltaPctOver(points, 14);
  const chartedBand = bandFor(charted, current);

  function selectCard(code: ApiIndexCode): void {
    setCharted(code);
    // Only repaint the map with something it can actually draw. On the live
    // console a thermal chip would paint an empty farm: thermal scenes carry
    // no grid cells, because `grid_configs` are per-product and only the
    // optical product has one.
    if (paintable.has(code)) onActiveIndexChange(code);
  }

  return (
    <div className="grid h-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
      <div className="flex min-h-0 flex-col gap-2">
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label={t(`dock.family.${family}`)}
        >
          {members.map((code, i) => (
            <IndexCard
              key={code}
              code={code}
              points={pointsByCode.get(code) ?? []}
              loading={seriesQs[i]?.isPending ?? true}
              unit={unitFor(code)}
              charted={charted === code}
              onMap={activeIndex === code}
              paintable={paintable.has(code)}
              onSelect={() => selectCard(code)}
            />
          ))}
        </div>

        {/* range */}
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            type="date"
            value={from}
            max={to}
            aria-label={t("dock.index.from")}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-ap-line bg-ap-panel px-2 py-1 text-xs text-ap-ink"
          />
          <span className="text-ap-muted" aria-hidden="true">
            →
          </span>
          <input
            type="date"
            value={to}
            min={from}
            aria-label={t("dock.index.to")}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-lg border border-ap-line bg-ap-panel px-2 py-1 text-xs text-ap-ink"
          />
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                setFrom(isoDaysBefore(p.days));
                setTo(isoDay());
              }}
              className="rounded-lg border border-ap-line px-2 py-1 text-xs font-semibold text-ap-muted hover:bg-ap-bg/60"
            >
              {t(`dock.index.preset.${p.key}`)}
            </button>
          ))}
        </div>

        {chartedQ?.isPending ? (
          <div className="h-[168px] animate-pulse rounded-xl bg-ap-line/40" />
        ) : chartedQ?.isError ? (
          <div className="flex h-[168px] items-center justify-center rounded-xl border border-ap-line text-sm text-ap-muted">
            {t("dock.index.error")}
          </div>
        ) : (
          <Chart points={points} unit={chartedUnit} />
        )}
        <div className="text-xs text-ap-muted">
          {`${t("dock.index.charting", { code: INDEX_META[charted].label })} · ${t("dock.index.footer", { count: points.length })}`}
        </div>
      </div>

      <div className="min-h-0 overflow-auto">
        <div className="flex items-end gap-4">
          <span className="text-3xl font-bold tabular-nums text-ap-ink">
            {formatIndexValue(current, chartedUnit)}
          </span>
          <span className="pb-1 text-xs">
            <span
              className="font-semibold tabular-nums"
              style={{
                color:
                  delta == null
                    ? HEALTH_DOT.unknown
                    : indexDeltaLabel(charted, delta / 100, null).color,
              }}
            >
              {delta == null ? "—" : `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`}
            </span>
            <br />
            <span className="text-ap-muted">{t("dock.index.over14d")}</span>
          </span>
        </div>

        {/* What the number above actually says about the block. */}
        {chartedBand ? (
          <div
            className="mt-2 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm text-ap-ink"
            style={{ borderColor: HEALTH_DOT[chartedBand.tone] }}
          >
            <Dot color={HEALTH_DOT[chartedBand.tone]} className="mt-1.5 shrink-0" />
            <span>{t(`dock.band.${charted}.${chartedBand.key}`)}</span>
          </div>
        ) : null}

        {/* Said once per tab rather than once per thermal card: the three
            thermal indices come from a 100 m sensor, so a block smaller than
            a hectare is reading its surroundings. */}
        {family === "thermal" ? (
          <p className="mt-2 text-xs text-ap-muted">
            {t("legend.thermalCaveat", { metres: THERMAL_METRES })}
          </p>
        ) : null}

        <div className="mt-3 text-xs font-bold uppercase tracking-wide text-ap-primary">
          {t("dock.inThisFamily")}
        </div>
        <div className="mt-1 flex flex-col gap-1.5">
          {members.map((code) => (
            <div key={code} className="rounded-xl border border-ap-line px-3 py-2 text-sm">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold text-ap-ink">{INDEX_META[code].label}</span>
                {/* Which satellite the number came from. This slot used to say
                    "Block level" or "Grid only", a distinction that no longer
                    exists — but sensor does, and it is the one that changes how
                    much weight a reading carries. */}
                <span className="text-xs text-ap-muted">
                  {isThermalIndex(code) ? t("dock.source.thermal") : t("dock.source.optical")}
                </span>
                {paintable.has(code) ? null : (
                  <span className="text-xs text-ap-muted">· {t("dock.notOnThisMap")}</span>
                )}
              </div>
              <dl className="mt-1 flex flex-col gap-1.5">
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-wide text-ap-muted">
                    {t("dock.definition")}
                  </dt>
                  <dd className="text-ap-ink/90">{t(`dock.def.${code}`)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-wide text-ap-muted">
                    {t("dock.howToRead")}
                  </dt>
                  <dd className="text-ap-muted">{t(`dock.scale.${code}`)}</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-ap-muted">{t("dock.readingCaveat")}</p>
      </div>
    </div>
  );
}
